import { useEffect, useMemo, useState } from 'react';
import { Bot, Check, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Skeleton } from 'components/ui/skeleton';
import { useTheme } from 'components/core/theme-provider';
import { useConfig } from 'hooks/use-config';
import { useDebounce } from 'hooks/use-debounce';
import { useGetAgentById, useGetAgents } from 'features/chat/hooks/use-agents';
import type { AgentSummary } from 'features/chat/agent-chat-module/types/agent.service.type';
import { cn } from 'lib/utils';

const AGENTS_LIMIT = 30;
const SEARCH_DEBOUNCE_MS = 300;

interface AgentSelectorProps {
  selectedAgentId: string | null;
  onSelect: (agentId: string | null) => void;
  isDisabled?: boolean;
}

const AgentLogo = ({ agent }: { agent: Pick<AgentSummary, 'logo_url'> }) =>
  agent.logo_url ? (
    <img
      src={agent.logo_url}
      alt=""
      className="h-4 w-4 shrink-0 rounded-full object-cover"
    />
  ) : (
    <Bot className="h-4 w-4 shrink-0" />
  );

export const AgentSelector = ({ selectedAgentId, onSelect, isDisabled }: AgentSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { configurationSettings } = useConfig();

  const projectKey = configurationSettings?.blocksKey || '';
  const trimmedSearch = debouncedSearch.trim();

  const { data: agentsData, isLoading, isFetching } = useGetAgents(
    {
      limit: AGENTS_LIMIT,
      offset: 0,
      project_key: projectKey,
      ...(trimmedSearch ? { name: trimmedSearch } : {}),
    },
    { enabled: !!projectKey, scope: 'agent-selector' },
  );

  const agents = useMemo(
    () => (agentsData?.agents ?? []).filter((a) => !a.is_archived && !a.is_disabled),
    [agentsData?.agents],
  );

  const selectedFromList = useMemo(
    () => agents.find((a) => a.id === selectedAgentId) ?? null,
    [agents, selectedAgentId],
  );

  const { data: selectedAgentData } = useGetAgentById(
    { id: selectedAgentId ?? '', projectKey },
    { enabled: !!selectedAgentId && !!projectKey && !selectedFromList },
  );

  const selectedAgent = useMemo(() => {
    if (selectedFromList) return selectedFromList;
    const agent = selectedAgentData?.agent;
    if (!agent || agent.id !== selectedAgentId) return null;
    return agent;
  }, [selectedFromList, selectedAgentData?.agent, selectedAgentId]);

  const noAgentLabel = t('NO_AGENT', { defaultValue: 'No agent' });
  const isSearching = isFetching || search.trim() !== trimmedSearch;

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  if (!isLoading && !trimmedSearch && agents.length === 0 && !selectedAgentId) return null;

  const textColor = theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]';

  const handleSelect = (agentId: string | null) => {
    onSelect(agentId);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild disabled={isDisabled}>
        <Button
          variant="ghost"
          disabled={isDisabled}
          role="combobox"
          aria-expanded={open}
          className={cn(
            'max-w-[140px] sm:max-w-[160px] md:max-w-[220px] min-w-0 justify-between gap-1 transition-colors duration-200 h-auto px-0 py-1 focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none hover:bg-transparent',
            textColor,
            isDisabled && 'opacity-50 cursor-not-allowed',
          )}
        >
          {selectedAgent && <AgentLogo agent={selectedAgent} />}
          <span
            className={cn(
              'text-sm font-normal leading-5 overflow-hidden text-ellipsis whitespace-nowrap min-w-0',
              textColor,
            )}
            title={selectedAgent?.name}
          >
            {selectedAgent?.name ?? noAgentLabel}
          </span>
          <ChevronDown className={cn('sm:h-4 sm:w-4 h-3 w-3 shrink-0', textColor)} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={12}
        onOpenAutoFocus={(event) => {
          if (window.matchMedia?.('(max-width: 639px)')?.matches) {
            event.preventDefault();
          }
        }}
        className="flex w-64 max-h-[min(18rem,var(--radix-popover-content-available-height))] flex-col overflow-hidden p-0"
      >
        <Command shouldFilter={false} className="max-h-full overflow-hidden">
          <CommandInput
            placeholder={t('SEARCH_AGENTS', { defaultValue: 'Search agents...' })}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="min-h-0 max-h-72 overflow-y-auto">
            {isSearching ? (
              <div className="space-y-1 p-1">
                {Array.from({ length: 8 }).map((_, index) => (
                  <div key={index} className="flex items-center gap-2 px-2 py-1.5">
                    <Skeleton className="h-4 w-4 shrink-0 rounded-full" />
                    <Skeleton className="h-4 flex-1" />
                  </div>
                ))}
              </div>
            ) : (
              <>
                <CommandEmpty>{t('NO_RESULTS_FOUND')}</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    value={noAgentLabel}
                    onSelect={() => handleSelect(null)}
                    className="gap-2"
                  >
                    <Bot className="!size-4 opacity-60" />
                    <span className="flex-1 truncate">{noAgentLabel}</span>
                    {!selectedAgentId && <Check className="!size-4 shrink-0" />}
                  </CommandItem>
                  {agents.map((agent) => (
                    <CommandItem
                      key={agent.id}
                      value={agent.name}
                      onSelect={() => handleSelect(agent.id)}
                      className="gap-2"
                    >
                      <AgentLogo agent={agent} />
                      <span className="flex-1 truncate" title={agent.name}>
                        {agent.name}
                      </span>
                      {selectedAgentId === agent.id && <Check className="!size-4 shrink-0" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
